import React from "react";
import { Mountain, Droplets, FlaskConical, Shield, Leaf, Heart } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getSpaBlurPlaceholder } from "@/utils/spaBlurPlaceholders";
import { GlassContainer, GlassCard } from "@/components/experimental/GlassUI";

const NatureAndScienceSection: React.FC = () => {
  const { t } = useLanguage();

  // Image grid - 2x3 layout
  const images = [
    {
      src: "/images/Homepage Shortlisted media/ Gallery 03 Clinique Nutraswiss/CS 6 massage room angle 3.high-quality.webp",
      alt: t("spa.natureScience.images.massage"),
    },
    {
      src: "/images/new masseuse.high-quality.optimized.webp",
      alt: t("spa.natureScience.images.therapist"),
    },
    {
      src: "/images/Homepage Shortlisted media/ Gallery 03 Clinique Nutraswiss/CS 3 massage room angle 1.high-quality.webp",
      alt: t("spa.natureScience.images.treatment"),
    },
    {
      src: "/images/Homepage Shortlisted media/ Gallery 03 Clinique Nutraswiss/a_vase_and_a_bowl_of_apples_on_a_table_at_Parkhote_50.optimized.webp",
      alt: t("spa.natureScience.images.wellness"),
    },
    {
      src: "/images/Homepage Shortlisted media/ Gallery 03 Clinique Nutraswiss/CS 2 panoromic balcony overlooking lake thun.high-quality.webp",
      alt: t("spa.natureScience.images.alpine"),
    },
    {
      src: "/images/Homepage Shortlisted media/ Gallery 03 Clinique Nutraswiss/cs7 massage room angle 3.webp",
      alt: t("spa.natureScience.images.relaxation"),
    },
  ];

  // Info blocks with icons
  const infoBlocks = [
    {
      icon: Mountain,
      title: t("spa.natureScience.blocks.air.title"),
      description: t("spa.natureScience.blocks.air.description"),
    },
    {
      icon: Droplets,
      title: t("spa.natureScience.blocks.water.title"),
      description: t("spa.natureScience.blocks.water.description"),
    },
    {
      icon: FlaskConical,
      title: t("spa.natureScience.blocks.science.title"),
      description: t("spa.natureScience.blocks.science.description"),
    },
  ];

  return (
    <section className="pt-32 pb-12 px-4 overflow-hidden" data-oid="nature-science-section">
      <div className="relative w-screen left-1/2 -translate-x-1/2" data-oid="nature-science-immersive-wrapper">

        {/* Premium Header - CONSISTENT WITH SAUNA SECTION */}
        <div className="max-w-7xl mx-auto flex flex-col items-center text-center mb-12 px-6 md:px-12" data-oid="nature-science-title">
          <GlassContainer intensity="low" className="px-10 py-8 mb-8 backdrop-blur-md">
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-playfair font-semibold mb-6 tracking-[0.2em] uppercase text-gray-900 drop-shadow-sm"
              data-oid="nature-science-heading"
            >
              {t("spa.natureScience.title")}
            </h2>
            <div className="w-24 h-px bg-alpine-gold mb-10 mx-auto" />
            <p
              className="text-lg md:text-xl text-gray-700 font-lato leading-relaxed max-w-4xl mx-auto italic"
              data-oid="nature-science-subtitle"
            >
              {t("spa.treatmentsAndServices.introduction")}
            </p>
          </GlassContainer>
        </div>

        <div className="max-w-7xl mx-auto px-4" data-oid="nature-science-container">

          {/* Image Grid - 3x2 with Premium Hover and Only Outer Rounded Corners */}
          <GlassContainer
            className="grid grid-cols-2 md:grid-cols-3 gap-[1px] mb-8 overflow-hidden"
            intensity="medium"
          >
            {images.map((image, index) => (
              <div
                key={index}
                className="relative overflow-hidden aspect-[16/10] group transition-all duration-500"
                data-oid={`nature-science-image-${index}`}
              >
                <OptimizedImage
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 opacity-90 group-hover:opacity-100"
                  blurDataURL={getSpaBlurPlaceholder(image.src)}
                  fetchPriority="auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                  <p className="text-white text-xs font-semibold uppercase tracking-widest drop-shadow-md">{image.alt}</p>
                </div>
              </div>
            ))}
          </GlassContainer>

          {/* Info Blocks - 3 columns with Premium Flourish */}
          <div
            className="grid md:grid-cols-3 gap-8 lg:gap-12"
            data-oid="nature-science-blocks"
          >
            {infoBlocks.map((block, index) => {
              const IconComponent = block.icon;
              return (
                <GlassCard
                  key={index}
                  className="flex flex-col items-center text-center group p-8"
                >
                  {/* Icon Circle */}
                  <div className="mb-8 relative" data-oid={`block-icon-${index}`}>
                    <div className="w-20 h-20 rounded-full bg-white/50 border border-white/40 shadow-inner flex items-center justify-center transition-all duration-500 group-hover:bg-alpine-gold group-hover:scale-110 group-hover:rotate-6">
                      <IconComponent
                        className="w-10 h-10 text-alpine-gold transition-colors duration-500 group-hover:text-white"
                        strokeWidth={1}
                      />
                    </div>
                  </div>
                  {/* Text content */}
                  <div className="flex-1" data-oid={`block-content-${index}`}>
                    <h3
                      className="text-2xl font-playfair font-semibold text-gray-900 mb-4 tracking-wide"
                      data-oid={`block-title-${index}`}
                    >
                      {block.title}
                    </h3>
                    <div className="w-8 h-px bg-alpine-gold/50 mx-auto mb-4 transition-all duration-500 group-hover:w-16 group-hover:bg-alpine-gold" />
                    <p
                      className="text-gray-700 leading-relaxed font-lato text-sm lg:text-base"
                      data-oid={`block-description-${index}`}
                    >
                      {block.description}
                    </p>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NatureAndScienceSection;
